import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUpRight, Linkedin, Twitter, Lock } from 'lucide-react';
import { footerLinks, contactInfo } from '../../data/mock';
import { useAuth } from '../../context/AuthContext';
import LoginModal from '../auth/LoginModal';

const Footer = () => {
  const [showLogin, setShowLogin] = useState(false);
  const { isAuthenticated } = useAuth();
  
  return (
    <footer className="bg-[#1a1c1b] text-white">
      {/* Login Modal */}
      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
      
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src="/favicon.webp" 
                alt="unternehmenbewerten.ch" 
                className="h-10 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              KI-gestützte Unternehmensbewertung für Schweizer KMU. 
              Schnell, anonym und zuverlässig.
            </p>
            <div className="flex gap-3">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Folgen Sie uns auf LinkedIn"
                className="w-10 h-10 rounded-full border border-gray-600 flex items-center justify-center text-gray-400 hover:text-[#FF0000] hover:border-[#FF0000] transition-colors"
              >
                <Linkedin size={18} aria-hidden="true" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Folgen Sie uns auf Twitter"
                className="w-10 h-10 rounded-full border border-gray-600 flex items-center justify-center text-gray-400 hover:text-[#FF0000] hover:border-[#FF0000] transition-colors"
              >
                <Twitter size={18} aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Leistungen */}
          <div>
            <h4 className="text-[#FF0000] font-semibold text-sm uppercase tracking-wider mb-6">Leistungen</h4>
            <ul className="space-y-3">
              {footerLinks.leistungen.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href} 
                    className="text-gray-400 hover:text-[#FF0000] transition-colors text-sm flex items-center gap-1 group"
                  >
                    {link.label}
                    <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Unternehmen */}
          <div>
            <h4 className="text-[#FF0000] font-semibold text-sm uppercase tracking-wider mb-6">Unternehmen</h4>
            <ul className="space-y-3">
              {footerLinks.unternehmen.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href} 
                    className="text-gray-400 hover:text-[#FF0000] transition-colors text-sm flex items-center gap-1 group"
                  >
                    {link.label}
                    <ArrowUpRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Kontakt */}
          <div>
            <h4 className="text-[#FF0000] font-semibold text-sm uppercase tracking-wider mb-6">Kontakt</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#FF0000] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm whitespace-pre-line">
                  {contactInfo.company}
                  {'\n'}{contactInfo.companyLine2}
                  {'\n'}{contactInfo.street}
                  {'\n'}{contactInfo.city}
                </span>
              </li>
              <li>
                <a 
                  href={`tel:${contactInfo.phone}`} 
                  className="flex items-center gap-3 text-gray-400 hover:text-[#FF0000] active:text-[#CC0000] transition-colors text-sm py-2 -my-2"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'contact_phone_click', {
                        event_category: 'contact',
                        method: 'phone',
                        source: 'footer'
                      });
                    }
                  }}
                >
                  <Phone size={18} className="text-[#FF0000] flex-shrink-0" />
                  {contactInfo.phone}
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${contactInfo.email}`} 
                  className="flex items-center gap-3 text-gray-400 hover:text-[#FF0000] active:text-[#CC0000] transition-colors text-sm py-2 -my-2"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.gtag) {
                      window.gtag('event', 'contact_email_click', {
                        event_category: 'contact',
                        method: 'email',
                        source: 'footer'
                      });
                    }
                  }}
                >
                  <Mail size={18} className="text-[#FF0000] flex-shrink-0" />
                  {contactInfo.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">
              © 2025 {contactInfo.company}. Alle Rechte vorbehalten.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              {footerLinks.rechtliches.map((link) => (
                <Link 
                  key={link.href} 
                  to={link.href} 
                  className="text-gray-500 hover:text-[#FF0000] active:text-[#CC0000] transition-colors text-sm py-2"
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Admin Login Button */}
              {isAuthenticated ? (
                <Link
                  to="/admin"
                  className="text-gray-500 hover:text-[#FF0000] active:text-[#CC0000] transition-colors p-2"
                  title="Admin-Bereich"
                  aria-label="Zum Admin-Bereich"
                  data-testid="admin-link"
                >
                  <Lock size={16} aria-hidden="true" />
                </Link>
              ) : (
                <button
                  onClick={() => setShowLogin(true)}
                  className="text-gray-500 hover:text-[#FF0000] active:text-[#CC0000] transition-colors p-2"
                  title="Admin-Login"
                  aria-label="Admin-Login öffnen"
                  data-testid="admin-login-btn"
                >
                  <Lock size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Extra padding for mobile CTA bar */}
      <div className="h-20 lg:hidden" aria-hidden="true" />
    </footer>
  );
};

export default Footer;
